import type { ModuleId, UserRole } from '../../../types';
import type { SendTier } from '../../../contexts/SchoolContext';

/**
 * The sections of Settings — the ONE list that both the rail and the routes
 * are drawn from, so a section cannot exist in one without the other.
 *
 * Data and pure functions only: App.tsx and Layout.tsx import this into the
 * main chunk, and the section components themselves stay lazy.
 *
 * Access here is about WHERE things are found. Who may write what is still
 * decided by firestore.rules and the callables, and each section is still the
 * one writer of its own config document.
 */

export type SettingsSectionId =
  | 'school' | 'term-dates' | 'modules'
  | 'behaviour' | 'leave' | 'send' | 'staff-absence' | 'marking' | 'wellbeing';

export interface SettingsContext {
  readonly role: UserRole;
  /** Module switched on for this school: entitled AND not hidden by the admin. */
  readonly hasModule: (id: ModuleId) => boolean;
  readonly sendTier: SendTier;
}

export interface SettingsSection {
  readonly id: SettingsSectionId;
  readonly label: string;
  /** Shown under the section heading — what used to be the page's subtitle. */
  readonly blurb: string;
  /** Who may OPEN the section by its address. */
  readonly roles: readonly UserRole[];
  /** Only when the school has this module switched on. */
  readonly module?: ModuleId;
  /** Rail only — narrower than `roles`. Cosmetic, never an access decision. */
  readonly listedFor?: (ctx: SettingsContext) => boolean;
}

const ADMIN: readonly UserRole[] = ['schoolAdmin'];
const STAFF: readonly UserRole[] = ['schoolAdmin', 'headteacher', 'lineManager', 'staff'];

export const SETTINGS_SECTIONS: readonly SettingsSection[] = [
  {
    id: 'school', label: 'School', roles: ADMIN,
    blurb: 'Your school’s name and crest, the MIS connection, and the terms you accepted.',
  },
  {
    id: 'term-dates', label: 'Term dates & calendar', roles: ADMIN,
    blurb: 'Term dates and INSET days, set once for the whole school, and the calendar staff see.',
  },
  {
    id: 'modules', label: 'Modules & Home', roles: ADMIN,
    blurb: 'What your school sees: which modules are on, which cards Home shows, and the emails that go out on a schedule.',
  },
  {
    id: 'behaviour', label: 'Behaviour & pastoral', roles: ADMIN, module: 'behaviour',
    blurb: 'Choose which behaviour and pastoral tools your school uses, then set up who hears about what. Staff are picked from your MIS via Wonde.',
  },
  {
    id: 'leave', label: 'Leave & cover', roles: ADMIN, module: 'leave',
    blurb: 'Set up your school day, who arranges cover, and who signs leave off. Staff are picked from their SchoolOne accounts, because everyone here has to sign in and act.',
  },
  // Reachable by any staff member with the module, exactly as /send/setup was:
  // the section self-gates, and a named SENCo is usually plain staff. Once a
  // SENCo exists only they may change the team, so the admin cannot be the only
  // person who can reach it. Listed for the admin and the named SENCo.
  {
    id: 'send', label: 'SEND', roles: STAFF, module: 'send',
    blurb: 'Who runs SEND at this school, and the deadlines the register works to.',
    listedFor: (c) => c.role === 'schoolAdmin' || c.sendTier === 'senco',
  },
  {
    id: 'staff-absence', label: 'Staff absence', roles: ADMIN, module: 'staffAbsence',
    blurb: 'Who may see staff absence, which measure your policy uses, and the points at which it says something happens. Which absence types count is set from the report pages.',
  },
  {
    id: 'marking', label: 'Marking', roles: ADMIN, module: 'marker',
    blurb: 'Defaults for Feedback Slips in Marking.',
  },
  {
    id: 'wellbeing', label: 'Wellbeing', roles: ADMIN, module: 'wellbeing',
    blurb: 'Who is asked for a wellbeing check-in.',
  },
];

export const SETTINGS_ROOT = '/school/settings';

export const settingsPath = (id: SettingsSectionId): string => `${SETTINGS_ROOT}/${id}`;

/** May this person open the section by its address? */
export const canOpen = (s: SettingsSection, c: SettingsContext): boolean =>
  s.roles.includes(c.role) && (!s.module || c.hasModule(s.module));

/** Should the rail show it? Narrower than canOpen, never wider. */
export const isListed = (s: SettingsSection, c: SettingsContext): boolean =>
  canOpen(s, c) && (s.listedFor?.(c) ?? true);
